import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { PaloConfirmationDialog } from '@synergy/components-palo';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, RefObject, useEffect, useState } from 'react';
import { SaveIcon, TrashIcon } from '@synergy/theme-next';
import { SubPageHandle } from '@synergy/utils';

import { PaloLanguageSwitch } from '../../../../../common/components/palo/palo-language-switch';
import { changeSelectedLanguage, resetSelectedLanguage } from '../../../../../common/actions/selected-language-actions';
import { selectedLanguageSelector } from '../../../../../common/selectors/selected-language-selectors';
import { supplierSpecialOffersDetailOverviewFormDataSelector } from '../overview/store/supplier-special-offers-detail-overview-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export interface PaloSupplierSpecialOffersHeaderProps {
    isNew: boolean;
    languageSwitchDisabled: boolean;
    subPageHandle: RefObject<SubPageHandle>;
    onSave: () => void;
    onDelete: () => void;
}

/**
 * Header actions and confirmations for the supplier special offers detail.
 * The screen owns no form of its own — its tabs do — so this cannot use the
 * shared content shell and plugs into PaloDetailView instead.
 */
export const usePaloSupplierSpecialOffersHeader = ({
    isNew,
    languageSwitchDisabled,
    subPageHandle,
    onSave,
    onDelete,
}: PaloSupplierSpecialOffersHeaderProps): { actions: ReactElement; dialogs: ReactElement } => {
    const dispatch = useThunkDispatch();
    const { formValues } = useAppSelector(supplierSpecialOffersDetailOverviewFormDataSelector);
    const selectedLocale = useAppSelector(selectedLanguageSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [desiredLocale, setDesiredLocale] = useState<Locale | null>(null);

    useEffect(() => {
        dispatch(resetSelectedLanguage());
    }, [dispatch]);

    // The open tab is saved before switching; only invalid input needs asking.
    const handleBeforeSwitch = (locale: Locale) => {
        if (locale === selectedLocale) {
            return false;
        }

        void (async () => {
            const errors = (await subPageHandle.current?.validate()) ?? {};
            if (Object.keys(errors).length === 0) {
                await subPageHandle.current?.submit();
                dispatch(changeSelectedLanguage(locale));
            } else {
                setDesiredLocale(locale);
            }
        })();

        return false;
    };

    const handleDiscardAndSwitch = () => {
        if (desiredLocale) {
            dispatch(changeSelectedLanguage(desiredLocale));
            subPageHandle.current?.reset();
        }
        setDesiredLocale(null);
    };

    const handleDelete = () => {
        setShowDeleteWarning(false);
        onDelete();
    };

    const actions = (
        <>
            {availableLocales.length > 1 && !languageSwitchDisabled && (
                <PaloLanguageSwitch onBeforeSwitch={handleBeforeSwitch} />
            )}
            <Button
                size="small"
                color="error"
                startIcon={<TrashIcon />}
                disabled={isNew}
                onClick={() => setShowDeleteWarning(true)}
            >
                <FormattedMessage id="app.common.delete" />
            </Button>
            <Button size="small" variant="contained" color="primary" startIcon={<SaveIcon />} onClick={onSave}>
                <FormattedMessage id="app.common.save" />
            </Button>
        </>
    );

    const dialogs = (
        <>
            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
                title={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.contentManagement.supplierSpecialOffers.deleteWarning"
                    values={{ name: formValues.title }}
                />
            </PaloConfirmationDialog>

            <PaloConfirmationDialog
                isVisible={!!desiredLocale}
                onCancel={() => setDesiredLocale(null)}
                onConfirm={handleDiscardAndSwitch}
                title={<FormattedMessage id="app.common.language.switch" />}
                action={<FormattedMessage id="app.common.deleteChanges" />}
            >
                <FormattedMessage id="app.contentManagement.supplierSpecialOffers.switchWarning" />
            </PaloConfirmationDialog>
        </>
    );

    return { actions, dialogs };
};
